import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { useToast } from "../../hooks/use-toast";
import { useDoctors } from "../../hooks/useDoctors";
import { useClinics } from "../../hooks/useClinics";

import {
  ViewModeSwitcher,
  type ViewMode,
} from "../../components/admin/appointment/view-mode-switcher";
import { SearchBar } from "../../components/admin/appointment/search-bar";
import { FilterPanel } from "../../components/admin/appointment/filter-panel";
import { EmptyState } from "../../components/admin/appointment/empty-state";

import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";

import {
  Stethoscope,
  Filter,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "../../lib/utils";

import type {
  AppointmentWithDetails,
  AppointmentFilters,
  Doctor,
  Room,
  AppointmentStatusType,
} from "../../shared/appointmentSchema";

import { AppointmentStatus } from "../../shared/appointmentSchema";
import {
  AppointmentsClient,
  BookingStatus,
  TimebandCode,
  type OrganizationBookingDto,
} from "../../web-api-client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { useNavigate } from "react-router-dom";
import { appConfig } from "../../appconfig";

const timebandRanges: Record<
  TimebandCode,
  { startTime: string; endTime: string; label: string }
> = {
  [TimebandCode.AM1]: { startTime: "07:00", endTime: "09:00", label: "Sáng 1" },
  [TimebandCode.AM2]: { startTime: "09:00", endTime: "11:00", label: "Sáng 2" },
  [TimebandCode.PM1]: {
    startTime: "13:30",
    endTime: "15:30",
    label: "Chiều 1",
  },
};

const mapBookingStatusToAppointment = (
  status?: BookingStatus,
): AppointmentStatusType => {
  switch (status) {
    case BookingStatus.Confirmed:
      return AppointmentStatus.CONFIRMED;
    case BookingStatus.Cancelled:
      return AppointmentStatus.CANCELLED;
    case BookingStatus.Held:
    default:
      return AppointmentStatus.PENDING;
  }
};

const mapAppointmentStatusToBooking = (
  status?: AppointmentStatusType,
): BookingStatus | undefined => {
  switch (status) {
    case AppointmentStatus.CONFIRMED:
      return BookingStatus.Confirmed;
    case AppointmentStatus.CANCELLED:
      return BookingStatus.Cancelled;
    case AppointmentStatus.PENDING:
      return BookingStatus.Held;
    default:
      return undefined;
  }
};

const resolveTimeRange = (band?: TimebandCode) => {
  if (!band) return { startTime: "--", endTime: "--", label: "--" };
  return (
    timebandRanges[band] ?? { startTime: "--", endTime: "--", label: "--" }
  );
};

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return "--";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm");
  } catch {
    return "--";
  }
};

const formatDateOnly = (value?: string | Date | null) => {
  if (!value) return "--";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return "--";
  }
};

function buildAppointmentsClient(navigate: ReturnType<typeof useNavigate>) {
  return new AppointmentsClient(appConfig.apiBaseUrl, {
    fetch: makeFetchWithAuth(fetchWithTimeout(), {
      onUnauthorized: () =>
        navigate("/login", {
          replace: true,
          state: { from: window.location.pathname },
        }),
    }),
  });
}

function useAppointments(params: {
  status?: BookingStatus;
  from?: string;
  to?: string;
  pageNumber: number;
  pageSize: number;
}) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: [
      "appointments",
      params.status ?? null,
      params.from ?? null,
      params.to ?? null,
      params.pageNumber,
      params.pageSize,
    ],
    queryFn: async () => {
      const client = buildAppointmentsClient(navigate);
      return await client.getApiAppointments(
        params.status,
        params.from ? new Date(params.from) : undefined,
        params.to ? new Date(params.to) : undefined,
        params.pageNumber,
        params.pageSize,
      );
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

function getStatusBadgeClasses(status?: AppointmentStatusType) {
  switch (status) {
    case AppointmentStatus.CONFIRMED:
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case AppointmentStatus.CANCELLED:
      return "bg-slate-100 text-slate-600 border-slate-200";
    case AppointmentStatus.PENDING:
    default:
      return "bg-amber-50 text-amber-700 border-amber-200";
  }
}

function getStatusText(status?: AppointmentStatusType) {
  switch (status) {
    case AppointmentStatus.CONFIRMED:
      return "Đã xác nhận";
    case AppointmentStatus.CANCELLED:
      return "Đã hủy";
    case AppointmentStatus.PENDING:
    default:
      return "Đang giữ";
  }
}

export default function Appointment() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const client = useMemo(() => buildAppointmentsClient(navigate), [navigate]);

  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<AppointmentFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);

  useEffect(() => {
    const storedView = localStorage.getItem("viewMode") as ViewMode | null;
    if (storedView) {
      setViewMode(storedView);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("viewMode", viewMode);
  }, [viewMode]);

  const statusFilter = mapAppointmentStatusToBooking(filters.status);

  const appointmentParams = useMemo(
    () => ({
      status: statusFilter,
      from: filters.date,
      to: filters.date,
      pageNumber: 1,
      pageSize: 500,
    }),
    [statusFilter, filters.date],
  );

  const {
    data: appointmentsPayload,
    isLoading: appointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useAppointments(appointmentParams);

  const { doctors: doctorDtos } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: "",
  });

  const { data: clinics = [] } = useClinics(false);

  const doctorOptions: Doctor[] = useMemo(
    () =>
      doctorDtos
        .filter((doctor) => doctor.doctorId)
        .map((doctor) => ({
          id: String(doctor.doctorId),
          name: doctor.name ?? "--",
          specialty: doctor.specialty ?? "",
          phone: null,
          email: null,
        })),
    [doctorDtos],
  );

  const roomOptions: Room[] = useMemo(
    () =>
      clinics
        .filter((clinic) => clinic.clinicId)
        .map((clinic) => ({
          id: String(clinic.clinicId),
          name: clinic.clinicName ?? `Phòng ${clinic.clinicId}`,
          floor: null,
          department: null,
        })),
    [clinics],
  );

  const doctorLookup = useMemo(() => {
    return new Map(doctorOptions.map((doctor) => [String(doctor.id), doctor]));
  }, [doctorOptions]);

  const roomLookup = useMemo(() => {
    return new Map(roomOptions.map((room) => [String(room.id), room]));
  }, [roomOptions]);

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ["appointments"] });
  };

  const confirmMutation = useMutation({
    mutationFn: async (id: number) => {
      setActingId(id);
      await client.postApiAppointmentsConfirm(id);
    },
    onSuccess: async () => {
      toast({
        title: "Đã xác nhận",
        description: "Lịch khám đã được xác nhận.",
      });
      await refreshAll();
    },
    onError: (error: any) => {
      toast({
        title: "Xác nhận thất bại",
        description: error?.message || "Không thể xác nhận lịch khám.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setActingId(null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: number) => {
      setActingId(id);
      await client.postApiAppointmentsCancel(id);
    },
    onSuccess: async () => {
      toast({
        title: "Đã hủy",
        description: "Lịch khám đã được hủy.",
      });
      await refreshAll();
    },
    onError: (error: any) => {
      toast({
        title: "Hủy thất bại",
        description: error?.message || "Không thể hủy lịch khám.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setActingId(null);
    },
  });

  const appointmentsData: AppointmentWithDetails[] = useMemo(() => {
    const items = appointmentsPayload?.items ?? [];

    return items.map((booking: OrganizationBookingDto) => {
      const { startTime, endTime } = resolveTimeRange(booking.timeband);
      const doctorId = booking.doctorId ? String(booking.doctorId) : undefined;
      const roomId = booking.roomId ? String(booking.roomId) : "";
      const patientKey = booking.patientKey ?? "Không rõ";

      const fallbackDoctor = doctorId
        ? (doctorLookup.get(doctorId) ?? null)
        : null;
      const fallbackRoom = roomLookup.get(roomId) ?? null;

      const doctor =
        doctorId || booking.doctorName
          ? {
              id: doctorId ?? "",
              name:
                booking.doctorName?.trim() ||
                fallbackDoctor?.name ||
                "Chưa gán",
              specialty: fallbackDoctor?.specialty ?? "",
              phone: null,
              email: null,
            }
          : null;

      const room =
        booking.roomName || roomId
          ? {
              id: roomId || "unknown",
              name:
                booking.roomName?.trim() ||
                fallbackRoom?.name ||
                (booking.roomId ? `Phòng ${booking.roomId}` : "Chưa gán"),
              floor: null,
              department: null,
            }
          : null;

      const bookingDate = booking.date
        ? format(booking.date as any, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      const timeDisplay =
        booking.timeDisplay?.trim() ||
        booking.appointmentTimeText?.trim() ||
        `${startTime} - ${endTime}`;

      return {
        id: String(booking.id ?? ""),
        patientId: patientKey,
        slotId: String(booking.scheduleSlotId ?? ""),
        doctorId: doctorId ?? null,
        roomId,
        date: bookingDate,
        startTime: booking.appointmentTimeText?.trim() || startTime,
        endTime: booking.appointmentTimeText?.trim() ? "" : endTime,
        status: mapBookingStatusToAppointment(booking.status),
        reason: booking.reasonForVisit ?? null,
        notes: booking.notes ?? null,
        createdAt: booking.created?.toISOString?.() ?? "",
        updatedAt: booking.lastModified?.toISOString?.() ?? "",
        hisAppointmentId: booking.hisAppointmentId ?? null,
        patient: {
          id: patientKey,
          name: booking.patientName?.trim() || "Chưa có tên",
          phone: booking.patientPhone?.trim() || patientKey,
          email: booking.patientEmail ?? null,
          dateOfBirth: null,
          address: null,
          notes: booking.notes ?? null,
        },
        doctor,
        room,
        timeDisplay,
      } as AppointmentWithDetails & { timeDisplay: string };
    });
  }, [appointmentsPayload, doctorLookup, roomLookup]);

  const filteredAppointments = useMemo(() => {
    if (!appointmentsData.length) return [];

    let result = [...appointmentsData];

    if (filters.date) {
      result = result.filter((apt) => apt.date === filters.date);
    }

    if (filters.doctorId) {
      result = result.filter((apt) => apt.doctorId === filters.doctorId);
    }

    if (filters.roomId) {
      result = result.filter((apt) => apt.roomId === filters.roomId);
    }

    if (filters.status) {
      result = result.filter((apt) => apt.status === filters.status);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (apt) =>
          apt.patient.name.toLowerCase().includes(query) ||
          apt.id.toLowerCase().includes(query) ||
          apt.doctor?.name?.toLowerCase().includes(query) ||
          apt.patient.phone.includes(query),
      );
    }

    return result.sort((a, b) => {
      const aCreated = new Date(a.createdAt || 0).getTime();
      const bCreated = new Date(b.createdAt || 0).getTime();
      return bCreated - aCreated;
    });
  }, [appointmentsData, filters, searchQuery]);

  const activeFilterCount = [
    filters.date,
    filters.doctorId,
    filters.roomId,
    filters.status,
  ].filter(Boolean).length;

  const isRowBusy = (id: string | number) =>
    actingId === Number(id) &&
    (confirmMutation.isPending || cancelMutation.isPending);

  const renderActionButtons = (item: AppointmentWithDetails) => {
    const busy = isRowBusy(item.id);
    const isConfirmed = item.status === AppointmentStatus.CONFIRMED;
    const isCancelled = item.status === AppointmentStatus.CANCELLED;

    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => confirmMutation.mutate(Number(item.id))}
          disabled={busy || isConfirmed}
          className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
        >
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {busy && !isCancelled ? "Đang xử lý..." : "Xác nhận"}
        </Button>

        <Button
          size="sm"
          variant="destructive"
          onClick={() => cancelMutation.mutate(Number(item.id))}
          disabled={busy || isCancelled}
        >
          <XCircle className="mr-2 h-4 w-4" />
          {busy && !isConfirmed ? "Đang xử lý..." : "Hủy"}
        </Button>
      </div>
    );
  };

  const renderTable = () => {
    return (
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">
            Danh sách lịch khám
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Hiển thị đơn giản, xác nhận trực tiếp ngoài danh sách
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr className="border-b">
                <th className="px-4 py-3 text-left font-medium">Mã lịch</th>
                <th className="px-4 py-3 text-left font-medium">Bệnh nhân</th>
                <th className="px-4 py-3 text-left font-medium">Ngày khám</th>
                <th className="px-4 py-3 text-left font-medium">Khung giờ</th>
                <th className="px-4 py-3 text-left font-medium">Phòng</th>
                <th className="px-4 py-3 text-left font-medium">Bác sĩ</th>
                <th className="px-4 py-3 text-left font-medium">Tạo lúc</th>
                <th className="px-4 py-3 text-left font-medium">Trạng thái</th>
                <th className="px-4 py-3 text-right font-medium">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((item) => (
                <tr
                  key={item.id}
                  className="border-b last:border-b-0 hover:bg-slate-50/60"
                >
                  <td className="px-4 py-3 font-medium">#{item.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">
                      {item.patient.name || "Chưa có tên"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {item.patient.phone || "--"}
                    </div>
                  </td>
                  <td className="px-4 py-3">{formatDateOnly(item.date)}</td>
                  <td className="px-4 py-3">
                    <div>{(item as any).timeDisplay || "--"}</div>
                  </td>
                  <td className="px-4 py-3">{item.room?.name ?? "Chưa gán"}</td>
                  <td className="px-4 py-3">
                    {item.doctor?.name ?? "Chưa gán"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateTime(item.createdAt as any)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
                        getStatusBadgeClasses(item.status),
                      )}
                    >
                      {getStatusText(item.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {renderActionButtons(item)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderList = () => {
    return (
      <div className="space-y-3">
        {filteredAppointments.map((item) => (
          <div
            key={item.id}
            className="rounded-xl border bg-white p-4 shadow-sm"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-base font-semibold text-slate-900">
                    #{item.id} - {item.patient.name || "Chưa có tên"}
                  </div>
                  <div className="text-sm text-slate-500">
                    {item.patient.phone || "--"}
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
                      getStatusBadgeClasses(item.status),
                    )}
                  >
                    {getStatusText(item.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                  <div>
                    <span className="font-medium text-slate-800">
                      Ngày khám:
                    </span>{" "}
                    {formatDateOnly(item.date)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">
                      Khung giờ:
                    </span>{" "}
                    {(item as any).timeDisplay || "--"}
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Phòng:</span>{" "}
                    {item.room?.name ?? "Chưa gán"}
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Bác sĩ:</span>{" "}
                    {item.doctor?.name ?? "Chưa gán"}
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-slate-800">Tạo lúc:</span>{" "}
                    {formatDateTime(item.createdAt as any)}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {renderActionButtons(item)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (appointmentsLoading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      );
    }

    if (appointmentsError) {
      return <EmptyState type="error" />;
    }

    if (filteredAppointments.length === 0 && searchQuery) {
      return <EmptyState type="no-results" />;
    }

    if (filteredAppointments.length === 0) {
      return <EmptyState type="no-appointments" />;
    }

    return viewMode === "table" ? renderTable() : renderList();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between gap-4 px-4 lg:px-6">
          <div className="flex items-center gap-2 text-primary">
            <Stethoscope className="h-6 w-6" />
            <span className="font-semibold text-lg hidden sm:inline">
              Quản lý Lịch Khám
            </span>
          </div>

          <div className="hidden md:flex">
            <ViewModeSwitcher value={viewMode} onChange={setViewMode} />
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetchAppointments()}
            disabled={appointmentsLoading}
          >
            <RefreshCw
              className={cn("h-4 w-4", appointmentsLoading && "animate-spin")}
            />
          </Button>
        </div>

        <div className="flex items-center gap-2 px-4 pb-4 md:hidden">
          <ViewModeSwitcher
            value={viewMode}
            onChange={setViewMode}
            className="flex-1"
          />
        </div>
      </header>

      <div className="flex">
        <aside className="hidden lg:block w-72 shrink-0 border-r bg-sidebar min-h-[calc(100vh-4rem)] sticky top-16">
          <div className="p-4">
            <div className="mb-4">
              <div className="text-sm font-semibold text-slate-900">Bộ lọc</div>
              <div className="text-xs text-slate-500 mt-1">
                Lọc theo ngày khám, bác sĩ, phòng khám và trạng thái
              </div>
            </div>

            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
              doctors={doctorOptions}
              rooms={roomOptions}
            />
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="p-4 lg:p-6 space-y-4">
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3 flex-wrap">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  className="flex-1 min-w-[240px]"
                />

                <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden gap-2">
                      <Filter className="h-4 w-4" />
                      Bộ lọc
                      {activeFilterCount > 0 && (
                        <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>

                  <SheetContent side="left" className="w-80">
                    <SheetHeader>
                      <SheetTitle>Bộ lọc</SheetTitle>
                    </SheetHeader>
                    <ScrollArea className="h-[calc(100vh-8rem)] mt-4">
                      <FilterPanel
                        filters={filters}
                        onFiltersChange={setFilters}
                        doctors={doctorOptions}
                        rooms={roomOptions}
                      />
                    </ScrollArea>
                  </SheetContent>
                </Sheet>

                <div className="text-sm text-muted-foreground hidden sm:block">
                  {filteredAppointments.length} lịch hẹn
                </div>
              </div>
            </div>

            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}